import React, {memo, useState, useEffect} from 'react'
import { Box, Typography, Button, IconButton, Tooltip, useMediaQuery, Backdrop } from '@mui/material';
import { ArrowRight, OrderInfo, OrderInfoActive, CloseIcon } from '../../assets/images/icons';
import PSIcon from '../../components/PSIcon';
import { getText } from '../../libs/utils';
import {styled, useTheme} from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';

const BottomBox = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
  margin: '30px 0 0',
  "&:empty": {
    display: 'none'
  },
  [theme.breakpoints.only('xs')]:{
    flexDirection: 'column',
    "button": {
      width: '100%'
    }
  }
}))

const OrdersDialogOrder = ({ order = {} }) => {
  const theme = useTheme();
  const [opened, setOpened] = useState(false);
  let IsXS = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    if(IsXS){
      opened === true
        ? document.body.style.overflow = 'hidden'
        : document.body.style.overflow = 'unset';
    }
  }, [opened])

  return (
    <>
    {IsXS && <Backdrop open={opened} sx={{zIndex: 1}}/>}
    <ClickAwayListener onClickAway={() => setOpened(false)}>
      <div>
        <Tooltip
          open={opened}
          classes={{
            tooltip: 'tooltip-order-details',
            popper: 'popper-order-details',
          }}
          onClose={() => setOpened(false)}
          PopperProps={{
            disablePortal: IsXS ? false : true,
            strategy: 'fixed',
            modifiers: [
              {
                name: 'flip',
                enabled: false
              },
            ],
          }}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement={IsXS ? 'bottom' : 'left-start'}
          title={
            <Box>
              <Box sx={{position: 'relative', padding: {sm: '0 0 14px'}}}>
                <Typography variant="h4" component="h4">{getText('orderInfo')}</Typography>
                <CloseIcon
                  aria-label="close"
                  onClick={() => setOpened(false)}
                  style={{
                    position: "absolute",
                    right: '-14px',
                    top: '-14px',
                    cursor: 'pointer'
                  }}
                />
              </Box>

              <Typography variant="smallCaption" component="div" sx={{ textTransform: 'uppercase', margin: {xs: '25px 0 18px', sm: '0 0 19px'} }}>
                {getText("accOrderUserData")}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'space-between'  }}>
                {order.clientDetails && order.clientDetails.length 
                  ? order.clientDetails.map((item) => (
                    <Box key={item.name}>
                      <Typography variant="boldLightCaption">{item.value}</Typography>
                      <Typography variant="greyCaption" component="div">{item.label}</Typography>
                    </Box>))
                  : null }

                {order.withdrawalTransactions && order.withdrawalTransactions[0] && order.withdrawalTransactions[0].outputs.map((item) => (
                  <Box key={item.name}>
                    <Typography variant="boldLightCaption" sx={{color: 'secondary.main'}}>{item.value}</Typography>
                    <Typography variant="greyCaption" component="div">{item.label}</Typography>
                  </Box>
                ))}

                {!order.clientDetails.length && !order.withdrawalTransactions.length
                  ? <Box><Typography variant="greyCaption" component="div">{getText("orderDataFieldsEmpty01")}</Typography></Box>
                  : null
                }
              </Box>

              <Typography variant="smallCaption" component="div" sx={{ textTransform: 'uppercase', margin: '32px 0 19px' }}>{getText("accOrderSellerData")}</Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'space-between' }}>
                {order.deposits && order.deposits[0] && order.deposits[0].tabs[0].outputs && order.deposits[0].tabs[0].outputs.length
                  ? order.deposits[0].tabs[0].outputs.map((item) => (
                    item.type === 'text' 
                      ? <Box key={item.name}>
                        <Typography variant="boldLightCaption">{item.value}</Typography>
                        <Typography variant="greyCaption" component="div">{item.label}</Typography>
                      </Box>
                      : <Typography key={item.name} variant="boldLightCaption">{item.value.paymentSystem}</Typography>
                  ))
                  : <Typography variant="greyCaption" component="div">{getText("orderDataFieldsEmpty01")}</Typography>
                }
              </Box>
    
              <Typography variant="smallCaption" component="div" sx={{ textTransform: 'uppercase', margin: '32px 0 18px' }}>{getText("accOrderRateData")}</Typography>
  
              <Box sx={{display: 'flex'}}>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <PSIcon
                    name={order.fromPaymentSystemClassName}
                    network={order.fromCurrencyNetwork}
                    height={32}
                    width={32}
                    margin="0 13px 0 0"
                  />
                  <Box>
                    <Typography variant="boldLightCaption" component="div">{order.rateValues.fromValue} {order.fromCurrency}</Typography>
                    <Typography variant="greyCaption" component="div">{order.fromPaymentSystem}</Typography>
                  </Box>
                </Box>
                <Box sx={{width: 50, display: 'flex', alignItems: 'center', justifyContent: 'center'}}><ArrowRight width="15" height="15" /></Box>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <PSIcon
                    name={order.toPaymentSystemClassName}
                    network={order.toCurrencyNetwork}
                    height={32}
                    width={32}
                    margin="0 13px 0 0"
                  />
                  <Box>
                    <Typography variant="boldLightCaption" component="div">{order.rateValues.toValue} {order.toCurrency}</Typography>
                    <Typography variant="greyCaption" component="div">{order.toPaymentSystem}</Typography>
                  </Box>
                </Box>
              </Box>

              <BottomBox>
                {order.transactionLink && <Box>
                  <Button 
                    onClick={() => window.open(order.transactionLink, '_blank')}
                    sx={{padding: '13px 25px'}}
                  >
                    {getText("accGoTransactionInfo")}
                  </Button>
                </Box>}

                {order.paymentStatus?.id === 3 && <Box>
                  <Button 
                    variant="contained" 
                    onClick={() => window.open(getText('linkBestchange'), '_blank')}
                    sx={{padding: '13px 25px'}}
                  >
                    {getText("accGiveFeedback")}
                  </Button>
                </Box>}
              </BottomBox>

            </Box>
          }
        >
          <Box
            sx={{ cursor: 'pointer', margin: { sm: '0 10px', md: '0' } }}
            onClick={() => setOpened(!opened)}
          >
            {opened ? <OrderInfoActive/> : <OrderInfo />}
          </Box>
        </Tooltip>
      </div>
    </ClickAwayListener>
    </>
  )
}

export default memo(OrdersDialogOrder)
// export default OrdersDialogOrder